import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { environment } from '../../environment';
import { PublicDeck } from '../../components/decks/render';
import { SAMPLE_PUBLIC_DECK, longDoc } from '../../components/decks/render/fixtures';
import { DeckLoad, deckPdfUrl, fetchHouseTheme, fetchPublicDeck } from './deck_viewer_api';
import { DeckMessage } from './components/DeckMessage';
import { Presenter } from './components/Presenter';
import { PrintDeck } from './components/PrintDeck';
import { SampleEditPlayground } from './components/SampleEditPlayground';

/** /d/__sample shows the fixture deck without the API (not in production builds); ?mode=edit for edit mode, ?long=1 for maximum-length copy. */
export const SAMPLE_TOKEN = '__sample';

type State = { state: 'loading' } | DeckLoad;

// #region useNoIndex
/** Shared decks are private links: keep them out of search engines. */
function useNoIndex(): void {
    React.useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
        return () => { meta.remove(); };
    }, []);
}

// #region DeckViewer
/**
 * /d/:token — the hosted deck a rep shares with a lead. Public: no login, no app chrome.
 * ?print=1 renders every slide stacked for the PDF renderer (no tracking, no controls).
 */
const DeckViewer: React.FunctionComponent<{}> = (): React.ReactElement => {
    const { token = '' } = useParams<{ token: string }>();
    const [params] = useSearchParams();
    const print = params.get('print') === '1';
    const sample = token === SAMPLE_TOKEN && !environment.production;
    // ?long=1 on the sample puts every field at its limit, to check that nothing overflows.
    const long = sample && params.get('long') === '1';
    const [load, setLoad] = React.useState<State>(() => (sample ? { ok: true, deck: long ? { ...SAMPLE_PUBLIC_DECK, doc: longDoc() } : SAMPLE_PUBLIC_DECK } : { state: 'loading' }));
    useNoIndex();

    // The sample deck wears the live brand (the same brand kit as marketing emails), so it previews what reps will send.
    React.useEffect(() => {
        if (!sample) return;
        let live = true;
        (async () => {
            const theme = await fetchHouseTheme();
            if (live && theme) setLoad((l) => ('ok' in l && l.ok ? { ok: true, deck: { ...l.deck, context: { ...l.deck.context, theme } } } : l));
        })();
        return () => { live = false; };
    }, [sample]);

    React.useEffect(() => {
        if (sample) return;
        let live = true;
        setLoad({ state: 'loading' });
        (async () => {
            const r = await fetchPublicDeck(token);
            if (live) setLoad(r);
        })();
        return () => { live = false; };
    }, [token, sample]);

    const deck: PublicDeck | null = 'ok' in load && load.ok ? load.deck : null;
    React.useEffect(() => { if (deck) document.title = deck.title; }, [deck]);
    React.useEffect(() => {
        if (print && 'ok' in load && !load.ok) window.__DECK_ERROR = load.status || 500;
    }, [print, load]);

    if (!('ok' in load)) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#f4f5f2]" aria-busy="true">
                <span className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#006c56]/20 border-t-[#006c56]" aria-label="Loading the deck" />
            </div>
        );
    }
    if (!load.ok) {
        if (load.status === 410) {
            const who = load.rep_name ? `Ask ${load.rep_name} for a new link` : 'Ask the person who sent it for a new link';
            return <DeckMessage title="This deck has expired" body={`${who}, and they can send you an up-to-date copy.`} />;
        }
        if (load.status === 404) return <DeckMessage title="We couldn’t find this deck" body="The link may be incomplete. Check it against the message it came in, or ask the sender to share it again." />;
        return <DeckMessage title="This deck didn’t load" body={load.message || 'Something went wrong on our side. Please try again in a moment.'} />;
    }
    if (print) return <PrintDeck deck={load.deck} autoPrint={params.get('autoprint') === '1'} />;
    if (sample && params.get('mode') === 'edit') return <SampleEditPlayground deck={load.deck} />;
    return <Presenter deck={load.deck} token={token} track={!sample} pdfUrl={sample ? `/d/${SAMPLE_TOKEN}?print=1` : deckPdfUrl(token)} />;
};

export default DeckViewer;
